<template>
  <div class="flex flex-col justify-center items-center">
    <div
      v-for="(line, il) in lines"
      :key="il"
      :class="`text-${textSize(line, lines.length)}`"
      class="flex flex-row justify-center items-center gap-1"
    >
      <Icon v-for="(icon, ii) in line.icons" :key="ii" :icon="icon.icon" />
      <div v-if="line.text.length > 0">{{ line.text }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  ActNoOp,
  ActKeyCode,
  ActMultipleKeyCodes,
  isKeyCode,
  isMultipleKeyCodes,
} from "@/keyboard";
import { useKeycapStore } from "@/stores";
import { KeyIconDisplay, Icon as KeyIcon } from "@/stores/keycap";

const props = defineProps<{
  action: ActNoOp | ActKeyCode | ActMultipleKeyCodes;
  max_length: number;
  max_lines: number;
  size_adjust: number;
}>();

const keycap = useKeycapStore();

const display = computed(() => {
  return keycap.displayMultiple(
    isKeyCode(props.action)
      ? [props.action.KeyCode]
      : isMultipleKeyCodes(props.action)
      ? props.action.MultipleKeyCodes
      : []
  );
});

type LineAccumulator = {
  text: string;
  icons: KeyIcon[];
};

const lineSize = (line: LineAccumulator) =>
  line.text.length +
  line.icons.length * 2 +
  (line.text.length > 0 && line.icons.length > 0 ? 1 : 0);

const displayedLines = (
  lines: LineAccumulator[],
  disp: KeyIconDisplay
): LineAccumulator[] => {
  let ommited = false;

  // Try to add icon/text to a new line or one of existing ones
  const addElem = (
    sizeRequired: number,
    add: (line: LineAccumulator) => void
  ) => {
    if (sizeRequired > props.max_length) {
      ommited = true;
      return;
    }

    // First try adding as a new line
    if (lines.length < props.max_lines) {
      const line = { text: "", icons: [] };
      add(line);
      lines.push(line);
      return;
    } else {
      // Try adding to the shortest line
      const line = lines.reduce((a, b) => (lineSize(a) < lineSize(b) ? a : b));

      if (lineSize(line) + sizeRequired <= props.max_length) {
        add(line);
        return;
      }
    }

    ommited = true;
  };

  disp.icon.forEach((icon) => addElem(2, (line) => line.icons.push(icon)));

  // Sort from longest to shortest for better inserting
  disp.text.sort((a, b) => b.length - a.length);

  disp.text.forEach((text) =>
    addElem(
      text.length + 1,
      (line) => (line.text += (line.text.length > 0 ? " " : "") + text)
    )
  );

  // Finally, if nothing was added (unlikely with icons) add the first text but trimmed
  if (lines.length == 0 && disp.text.length > 0) {
    ommited = false; // no need to add it later
    const text = disp.text[0].slice(0, props.max_length - 1) + "…";
    lines.push({ text, icons: [] });
  }

  // Add 3 dots to the shortest line
  if (ommited) {
    if (lines.length == 0) lines.push({ text: "", icons: [] });
    const shortest = lines.reduce((a, b) =>
      lineSize(a) < lineSize(b) ? a : b
    );
    shortest.text += "…";
  }

  // Sort lines from shortest to longest as it looks better
  lines.sort((a, b) => lineSize(a) - lineSize(b));

  return lines;
};

const lines = computed(() => {
  const combined = {
    text: display.value.keys.text.concat(display.value.mods.text),
    icon: display.value.keys.icon.concat(display.value.mods.icon),
  };
  return displayedLines([], combined);
});

const textSize = (line: LineAccumulator, nlines: number) => {
  // const size = lineSize(line) + Math.ceil(nlines / 2);
  const size = lineSize(line) + Math.ceil(nlines / 2) - props.size_adjust;
  // console.log(size, size2);
  return Math.max(1, Math.min(size, 6));
};
</script>

<style scoped>
.text-1 {
  font-size: 1em;
}
.text-2 {
  font-size: 0.92em;
}
.text-3 {
  font-size: 0.84em;
}
.text-4 {
  font-size: 0.76em;
}
.text-5 {
  font-size: 0.68em;
}
.text-6 {
  font-size: 0.6em;
}
.text-6 {
  font-size: 0.52em;
}
</style>
